import DOMPurify from 'dompurify';

const globalAttrs = new Set([
	'accessKey',
	'autoCapitalize',
	'className',
	'contentEditable',
	'dir',
	'draggable',
	'hidden',
	'id',
	'inputMode',
	'is',
	'itemId',
	'itemProp',
	'itemRef',
	'itemScope',
	'itemType',
	'lang',
	'nonce',
	'slot',
	'spellCheck',
	'style',
	'tabIndex',
	'title',
	'translate',
]);

export const splitGlobalAttrs = attrs => {
	let other = {...attrs};
	let global = {};
	Object.keys(other).forEach(k => {
		if (globalAttrs.has(k) || k.startsWith('data-') || k.startsWith('aria-')) {
			global[k] = other[k];
			delete other[k];
		}
	});
	return [global, other];
};

// Basic notice formatting, including legacy bold/italic and Quill strikethrough.
// Hrefs are limited to http, https, mailto and relative URLs.
// (DOMPurify's default uri pattern with the scheme list narrowed down)
const noticeSanitizeConfig = {
	ALLOWED_TAGS: [
		'a',
		'strong',
		'b',
		'em',
		'i',
		's',
		'strike',
		'br',
		'p',
		'u',
		'ul',
		'ol',
		'li',
	],
	ALLOWED_ATTR: ['href', 'target', 'rel'],
	ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
	ALLOW_DATA_ATTR: false,
	ALLOW_ARIA_ATTR: false,
};

const noticeAllowedRels = ['noopener', 'noreferrer', 'nofollow'];

// own instance so that the hook below doesn't affect other DOMPurify users
const noticePurify = DOMPurify();

// Quill's semantic HTML uses non-breaking spaces for every space. Normalize only
// text nodes so both new and persisted notices wrap without changing link URLs.
noticePurify.addHook('beforeSanitizeElements', node => {
	if (node.nodeType === Node.TEXT_NODE) {
		node.textContent = node.textContent.replace(/\u00a0/g, ' ');
	}
});

// Attributes are allowed only on links, target only as _blank and rel only with the values above.
// _blank links always get noopener
noticePurify.addHook('afterSanitizeAttributes', node => {
	if (node.nodeName !== 'A') {
		['href', 'target', 'rel'].forEach(attr => node.removeAttribute(attr));
		return;
	}

	// Bare domains entered in Quill otherwise resolve as paths on the app's host.
	const href = node.getAttribute('href')?.trim();
	if (
		href &&
		/^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}(?::\d+)?(?:[/?#]|$)/i.test(href)
	) {
		node.setAttribute('href', `https://${href}`);
	}

	const isBlank = node.getAttribute('target') === '_blank';
	if (!isBlank) node.removeAttribute('target');

	const rels = (node.getAttribute('rel') || '')
		.toLowerCase()
		.split(/\s+/)
		.filter(rel => noticeAllowedRels.includes(rel));
	if (isBlank && !rels.includes('noopener')) rels.push('noopener');

	if (rels.length) {
		node.setAttribute('rel', [...new Set(rels)].join(' '));
	} else {
		node.removeAttribute('rel');
	}
});

// notice content is stored as html and may contain anything, so sanitize it before rendering
export const sanitizeNoticeHtml = html =>
	html == null ? '' : noticePurify.sanitize(html, noticeSanitizeConfig);

// Table previews need decoded text and separators between paragraphs/list items.
export const noticeHtmlToText = html => {
	const template = document.createElement('template');
	template.innerHTML = sanitizeNoticeHtml(html);
	template.content.querySelectorAll('p, li, br').forEach(node => {
		node.after(document.createTextNode(' '));
	});
	return template.content.textContent.replace(/\s+/g, ' ').trim();
};
